import { useState, useEffect, useRef } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  XMarkIcon,
  DevicePhoneMobileIcon,
  QrCodeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import { paymentService } from '../../services/paymentService'
import toast from 'react-hot-toast'

interface MpesaPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  amount: number
  saleId?: number
  customerId?: number
  onPaymentComplete?: (payment: any) => void
}

type PaymentMode = 'stk_push' | 'transaction_code'
type PaymentStatus = 'idle' | 'processing' | 'pending' | 'completed' | 'failed'

export const MpesaPaymentModal = ({
  isOpen,
  onClose,
  amount,
  saleId,
  customerId,
  onPaymentComplete,
}: MpesaPaymentModalProps) => {
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('stk_push')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [transactionCode, setTransactionCode] = useState('')
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle')
  const [checkoutRequestId, setCheckoutRequestId] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState('')
  const statusCheckInterval = useRef<NodeJS.Timeout | null>(null)

  // Get M-Pesa config
  const { data: mpesaConfig } = useQuery({
    queryKey: ['mpesa-config'],
    queryFn: () => paymentService.getMpesaConfig(),
    enabled: isOpen,
  })

  // STK Push mutation
  const stkPushMutation = useMutation({
    mutationFn: (data: {
      phone_number: string
      amount: number
      account_reference?: string
      description?: string
      sale_id?: number
      customer_id?: number
    }) => paymentService.initiateMpesaSTK(data),
    onSuccess: (response) => {
      const data = response.data.data
      setCheckoutRequestId(data.checkout_request_id)
      setPaymentStatus('pending')
      setStatusMessage(data.message || 'STK push sent to your phone. Please enter your M-Pesa PIN.')
      toast.success('STK push sent successfully')
    },
    onError: (error: any) => {
      setPaymentStatus('failed')
      setStatusMessage(error.response?.data?.message || 'Failed to initiate M-Pesa payment')
      toast.error('Failed to send STK push')
    },
  })

  // Validate transaction code mutation
  const validateCodeMutation = useMutation({
    mutationFn: (data: { mpesa_receipt_number: string; expected_amount: number }) =>
      paymentService.validateMpesaCode(data),
  })

  // Complete payment with transaction code mutation
  const completeWithCodeMutation = useMutation({
    mutationFn: (data: {
      mpesa_receipt_number: string
      phone_number?: string
      amount: number
      sale_id?: number
      customer_id?: number
      payment_method_id: number
    }) => paymentService.completeWithMpesaCode(data),
    onSuccess: (response) => {
      setPaymentStatus('completed')
      setStatusMessage('Payment completed successfully!')
      toast.success('Payment completed')
      onPaymentComplete?.(response.data.data)
      setTimeout(() => {
        onClose()
        resetState()
      }, 2000)
    },
    onError: (error: any) => {
      setPaymentStatus('failed')
      setStatusMessage(error.response?.data?.message || 'Failed to complete payment')
      toast.error('Payment failed')
    },
  })

  // Poll for status when in pending state
  useEffect(() => {
    if (paymentStatus === 'pending' && checkoutRequestId) {
      statusCheckInterval.current = setInterval(async () => {
        try {
          const response = await paymentService.checkMpesaStatus(checkoutRequestId)
          const data = response.data.data

          if (data.status === 'completed') {
            setPaymentStatus('completed')
            setStatusMessage(`Payment completed! Receipt: ${data.receiptNumber}`)
            toast.success('Payment received')
            onPaymentComplete?.(response.data.data)
            if (statusCheckInterval.current) {
              clearInterval(statusCheckInterval.current)
            }
            setTimeout(() => {
              onClose()
              resetState()
            }, 2000)
          } else if (data.status === 'failed') {
            setPaymentStatus('failed')
            setStatusMessage(data.message || 'Payment failed')
            toast.error('Payment failed')
            if (statusCheckInterval.current) {
              clearInterval(statusCheckInterval.current)
            }
          }
        } catch (error) {
          console.error('Status check error:', error)
        }
      }, 5000) // Check every 5 seconds

      // Stop checking after 2 minutes
      setTimeout(() => {
        if (statusCheckInterval.current) {
          clearInterval(statusCheckInterval.current)
          if (paymentStatus === 'pending') {
            setPaymentStatus('failed')
            setStatusMessage('Payment timed out. Please try again or use transaction code.')
          }
        }
      }, 120000)
    }

    return () => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current)
      }
    }
  }, [paymentStatus, checkoutRequestId])

  const resetState = () => {
    setPaymentMode('stk_push')
    setPhoneNumber('')
    setTransactionCode('')
    setPaymentStatus('idle')
    setCheckoutRequestId(null)
    setStatusMessage('')
    if (statusCheckInterval.current) {
      clearInterval(statusCheckInterval.current)
    }
  }

  const handleClose = () => {
    if (statusCheckInterval.current) {
      clearInterval(statusCheckInterval.current)
    }
    onClose()
    resetState()
  }

  const handleSTKPush = () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number')
      return
    }

    setPaymentStatus('processing')
    setStatusMessage('Sending STK push to your phone...')

    stkPushMutation.mutate({
      phone_number: phoneNumber,
      amount,
      account_reference: saleId ? `SALE-${saleId}` : undefined,
      description: `Payment of KSh ${amount.toLocaleString()}`,
      sale_id: saleId,
      customer_id: customerId,
    })
  }

  const handleTransactionCode = async () => {
    if (!transactionCode || transactionCode.length < 8) {
      toast.error('Please enter a valid M-Pesa transaction code')
      return
    }

    setPaymentStatus('processing')
    setStatusMessage('Validating transaction code...')

    try {
      // First validate the code
      const validationResult = await validateCodeMutation.mutateAsync({
        mpesa_receipt_number: transactionCode,
        expected_amount: amount,
      })

      const validation = validationResult.data.data

      if (!validation.valid) {
        setPaymentStatus('failed')
        setStatusMessage(validation.message || 'Invalid transaction code')
        toast.error('Validation failed')
        return
      }

      // If amount doesn't match, confirm with user
      if (validation.amount && validation.amount !== amount) {
        const confirmed = window.confirm(
          `The transaction amount (KSh ${validation.amount.toLocaleString()}) doesn't match the expected amount (KSh ${amount.toLocaleString()}). Do you want to continue?`
        )
        if (!confirmed) {
          setPaymentStatus('idle')
          return
        }
      }

      setStatusMessage('Completing payment...')

      // Complete the payment
      completeWithCodeMutation.mutate({
        mpesa_receipt_number: transactionCode,
        phone_number: phoneNumber || undefined,
        amount: validation.amount || amount,
        sale_id: saleId,
        customer_id: customerId,
        payment_method_id: 2, // Assuming M-Pesa is method_id 2
      })
    } catch (error: any) {
      setPaymentStatus('failed')
      setStatusMessage(error.response?.data?.message || 'Failed to validate transaction code')
    }
  }

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '')
    
    // Format based on length
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`
  }

  if (!isOpen) return null

  const isMpesaEnabled = mpesaConfig?.data?.data?.enabled ?? true

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
          {/* Header */}
          <div className="bg-green-600 px-4 py-4 sm:px-6 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <DevicePhoneMobileIcon className="w-6 h-6 text-white" />
              <div>
                <h3 className="text-lg font-medium text-white">M-Pesa Payment</h3>
                <p className="text-sm text-green-100">
                  Amount: <span className="font-bold">KSh {amount.toLocaleString()}</span>
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-green-100 hover:text-white transition-colors"
              disabled={paymentStatus === 'processing' || paymentStatus === 'pending'}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6">
            {!isMpesaEnabled && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700">
                  M-Pesa integration is not fully configured. You can still enter transaction codes manually.
                </p>
              </div>
            )}

            {/* Payment Mode Tabs */}
            {paymentStatus === 'idle' && (
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setPaymentMode('stk_push')}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium text-sm transition-colors ${
                    paymentMode === 'stk_push'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <DevicePhoneMobileIcon className="w-5 h-5" />
                    <span>STK Push</span>
                  </div>
                </button>
                <button
                  onClick={() => setPaymentMode('transaction_code')}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium text-sm transition-colors ${
                    paymentMode === 'transaction_code'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <QrCodeIcon className="w-5 h-5" />
                    <span>Enter Code</span>
                  </div>
                </button>
              </div>
            )}

            {/* Status Display */}
            {paymentStatus !== 'idle' && (
              <div className="mb-6 text-center">
                {paymentStatus === 'processing' && (
                  <div className="flex flex-col items-center">
                    <ArrowPathIcon className="w-12 h-12 text-green-600 animate-spin mb-3" />
                    <p className="text-gray-600">{statusMessage}</p>
                  </div>
                )}
                {paymentStatus === 'pending' && (
                  <div className="flex flex-col items-center">
                    <ClockIcon className="w-12 h-12 text-yellow-500 mb-3" />
                    <p className="text-gray-600 mb-2">{statusMessage}</p>
                    <p className="text-sm text-gray-500">
                      Waiting for payment confirmation...
                    </p>
                    <button
                      onClick={() => {
                        setPaymentStatus('idle')
                        setPaymentMode('transaction_code')
                        if (statusCheckInterval.current) {
                          clearInterval(statusCheckInterval.current)
                        }
                      }}
                      className="mt-3 text-sm text-green-600 hover:underline"
                    >
                      Enter transaction code instead
                    </button>
                  </div>
                )}
                {paymentStatus === 'completed' && (
                  <div className="flex flex-col items-center">
                    <CheckCircleIcon className="w-12 h-12 text-green-500 mb-3" />
                    <p className="text-green-600 font-medium">{statusMessage}</p>
                  </div>
                )}
                {paymentStatus === 'failed' && (
                  <div className="flex flex-col items-center">
                    <ExclamationCircleIcon className="w-12 h-12 text-red-500 mb-3" />
                    <p className="text-red-600">{statusMessage}</p>
                    <button
                      onClick={() => setPaymentStatus('idle')}
                      className="mt-3 text-sm text-green-600 hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* STK Push Form */}
            {paymentStatus === 'idle' && paymentMode === 'stk_push' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    M-Pesa Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g. 0712 345 678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                    maxLength={13}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the M-Pesa registered phone number that will make the payment
                  </p>
                </div>

                <button
                  onClick={handleSTKPush}
                  disabled={stkPushMutation.isPending || phoneNumber.replace(/\D/g, '').length < 10}
                  className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  {stkPushMutation.isPending ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <DevicePhoneMobileIcon className="w-5 h-5" />
                      Send STK Push
                    </>
                  )}
                </button>

                <div className="text-center">
                  <button
                    onClick={() => setPaymentMode('transaction_code')}
                    className="text-sm text-green-600 hover:underline"
                  >
                    Already paid? Enter transaction code
                  </button>
                </div>
              </div>
            )}

            {/* Transaction Code Form */}
            {paymentStatus === 'idle' && paymentMode === 'transaction_code' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    M-Pesa Transaction Code
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. QN7R8W9X1Y"
                    value={transactionCode}
                    onChange={(e) => setTransactionCode(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-mono uppercase"
                    maxLength={12}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the M-Pesa confirmation code from your SMS
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g. 0712 345 678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    maxLength={13}
                  />
                </div>

                <button
                  onClick={handleTransactionCode}
                  disabled={
                    completeWithCodeMutation.isPending ||
                    transactionCode.length < 8
                  }
                  className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  {completeWithCodeMutation.isPending ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      Verify & Complete
                    </>
                  )}
                </button>

                <div className="text-center">
                  <button
                    onClick={() => setPaymentMode('stk_push')}
                    className="text-sm text-green-600 hover:underline"
                  >
                    Request STK push instead
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6">
            <p className="text-xs text-center text-gray-500">
              Secure M-Pesa payment powered by Safaricom
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
